/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
    if (!this.container) return;

    this.form = this.container.querySelector('form.searchform');
    this.button = document.querySelectorAll('.thb-quick-search');
    this.input = this.container.querySelector('input[type="search"]');
    this.defaultTab = this.container.querySelector('.side-panel-content--initial');
    this.predictiveSearchResults = this.container.querySelector('.side-panel-content--has-tabs');

    this.abortController = null;
    this.lastRenderedQueryKey = null;
    // Cache pre-built DOM containers (not HTML strings) to avoid reparsing on render.
    this.resultsCache = new Map();

    this.onInput = debounce(this.onChange.bind(this), 300);

    this.setupEventListeners();
  }

  /**
   * Replace children by cloning from an existing element (avoids innerHTML string parsing).
   * @param {Element} target
   * @param {Element} source
   */
  static replaceChildrenFromElement(target, source) {
    if (!target || !source) return;
    const children = Array.from(source.childNodes).map((node) => node.cloneNode(true));
    target.replaceChildren(...children);
  }

  setupEventListeners() {
    if (!this.form || !this.input) return;

    this.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', this.onInput);

    this.button.forEach((item, i) => {
      item.addEventListener('click', (event) => {
        var _this = this;
        event.preventDefault();
        // Prevent heavy document-level click handlers (e.g. jQuery) from running for this interaction.
        event.stopPropagation();
        // Keep the click handler lightweight to reduce INP; do DOM/state work in the next task.
        setTimeout(() => {
          const body = document.body;
          body.classList.toggle('open-cc');
          this.container.classList.toggle('active');

          if (this.container.classList.contains('active')) {
            setTimeout(function() {
              _this.input.focus({
                preventScroll: true
              });
            }, 100);
            dispatchCustomEvent('search:open');
          }
        }, 0);

        return false;
      });
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    if (!this.predictiveSearchResults) return;

    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.predictiveSearchResults.classList.remove('active');
      this.predictiveSearchResults.classList.remove('loading');
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      return;
    }
    this.predictiveSearchResults.classList.add('active');
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (!this.predictiveSearchResults) return;

    const cachedContainer = this.resultsCache.get(queryKey);
    if (cachedContainer) {
      // Defer DOM work to the next task to reduce paint-blocking work within the same interaction.
      setTimeout(() => this.renderSearchResultsFromContainer(cachedContainer, queryKey), 0);
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.predictiveSearchResults.classList.add('loading');

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`, { signal })
      .then((response) => {
        this.predictiveSearchResults.classList.remove('loading');
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (signal?.aborted) return;

        // Defer parsing + DOM updates to the next task so the browser can paint between async steps.
        setTimeout(() => {
          if (signal?.aborted) return;
          if (!this.predictiveSearchResults) return;

          const parsedDoc = PredictiveSearch.domParser.parseFromString(text, 'text/html');
          const section = parsedDoc.querySelector('#shopify-section-predictive-search');
          if (!section) return;

          // Build a cached DOM container from the already-parsed response DOM to avoid innerHTML reparsing.
          const container = document.createElement('div');
          PredictiveSearch.replaceChildrenFromElement(container, section);

          this.resultsCache.set(queryKey, container);
          this.renderSearchResultsFromContainer(container, queryKey);
        }, 0);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        this.predictiveSearchResults?.classList.remove('loading');
        throw error;
      });
  }

  renderSearchResultsFromContainer(container, queryKey) {
    // Avoid re-rendering identical markup for the same query (helps reduce main-thread work during typing).
    if (queryKey && queryKey === this.lastRenderedQueryKey) return;
    this.lastRenderedQueryKey = queryKey || null;

    PredictiveSearch.replaceChildrenFromElement(this.predictiveSearchResults, container);

    let _this = this,
      submitButton = this.container.querySelector('#search-results-submit');


    if (submitButton) {
      submitButton.addEventListener('click', () => {
        _this.form.submit();
      });
    }
  }

  close() {
    this.container.classList.remove('active');
  }
}

PredictiveSearch.domParser = new DOMParser();
window.addEventListener('DOMContentLoaded', () => {
  if (typeof PredictiveSearch !== 'undefined') {
    new PredictiveSearch();
  }
});